import { Navigate, useSearchParams } from 'react-router';
import { Badge, HorizontalContainer, VerticalContainer } from '@/components';
import { GreenDotIcon, OrangeCheckIcon, RedDotIcon } from '@/components/Icons';
import type { CouponRedemptionViewModel } from '@/features/profile/model/types';
import { R } from '@/features/profile/resources/ProfileResources.resx';
import { ServiceTypeEnum, getEnumTranslationByKey } from '@/shared/enums';

type CouponDetailsProps = {
    couponRedemptions: CouponRedemptionViewModel[];
};

export const CouponDetails = ({ couponRedemptions }: CouponDetailsProps) => {
    const [searchParams] = useSearchParams();
    const coupon = couponRedemptions.find(
        (redemption) =>
            redemption.CouponRedemptionId.toString() ===
            searchParams.get('couponId'),
    );

    if (!coupon) {
        return <Navigate to="/Access" replace />;
    }

    return (
        <VerticalContainer spacing="md">
            <HorizontalContainer spacing="xs" className="items-center text-sm">
                {coupon.IsActive && <GreenDotIcon className="size-2" />}
                {!coupon.IsActive && <RedDotIcon className="size-2" />}
                <span>{coupon.StatusText}</span>
            </HorizontalContainer>

            {(coupon.ShowConsultationsUsed || coupon.ShowDiscount) && (
                <HorizontalContainer spacing="md">
                    {coupon.ShowConsultationsUsed && (
                        <Badge type="information">
                            {coupon.ConsultationsUsedText}
                        </Badge>
                    )}
                    {coupon.ShowDiscount && (
                        <Badge type="promo">{coupon.DiscountText}</Badge>
                    )}
                </HorizontalContainer>
            )}

            <VerticalContainer spacing="sm">
                <h2 id="heading-CouponDetails">{coupon.CouponName}</h2>
                {coupon.CouponRedemptionDescription && (
                    <div className="leading-normal">
                        {coupon.CouponRedemptionDescription}
                    </div>
                )}
                {coupon.CompanyName && (
                    <div className="leading-normal text-(--color-text-secondary)">
                        {R.CouponCompany}: {coupon.CompanyName}
                    </div>
                )}
                <div className="text-lg leading-normal text-(--color-text-secondary)">
                    {R.IssuedTo}: {coupon.CouponRecipientName}
                </div>
                {coupon.ExpiryDateString && (
                    <div className="leading-normal text-(--color-text-secondary)">
                        {coupon.ExpiryDateString}
                    </div>
                )}
            </VerticalContainer>

            <VerticalContainer spacing="sm">
                {coupon.ServiceTypes.map((serviceType) => (
                    <HorizontalContainer
                        key={serviceType}
                        spacing="sm"
                        className="items-center"
                    >
                        <OrangeCheckIcon />
                        <span>
                            {getEnumTranslationByKey(
                                ServiceTypeEnum,
                                serviceType,
                            )}
                        </span>
                    </HorizontalContainer>
                ))}
            </VerticalContainer>
        </VerticalContainer>
    );
};
